<div class="admin-container">
    <!-- Header -->
    <header class="admin-header">
        <div class="header-content">
            <div class="title-section">
                <h1 class="page-title">Admin</h1>
                <p class="page-subtitle">Manage users and invitations</p>
            </div>
        </div>
    </header>

    <!-- Invite Form Section -->
    <section class="invite-section">
        <h2 class="section-title">
            <mat-icon>person_add</mat-icon>
            Invite New User
        </h2>
        <div class="invite-form">
            <mat-form-field class="invite-field" appearance="outline">
                <mat-label>Email Address</mat-label>
                <input matInput type="email" [formControl]="inviteEmailControl"
                    placeholder="user@realtime-innovations.com" />
                <mat-icon matSuffix>email</mat-icon>
                @if (inviteEmailControl.hasError('required') && inviteEmailControl.touched) {
                <mat-error>Email is required</mat-error>
                }
                @if (inviteEmailControl.hasError('email') && inviteEmailControl.touched) {
                <mat-error>Please enter a valid email</mat-error>
                }
            </mat-form-field>
            <mat-form-field class="invite-field name-field" appearance="outline">
                <mat-label>Name (Optional)</mat-label>
                <input matInput type="text" [formControl]="inviteNameControl" placeholder="John Doe" />
                <mat-icon matSuffix>person</mat-icon>
            </mat-form-field>
            <button class="btn-invite" (click)="sendInvite()" [disabled]="isInviting() || inviteEmailControl.invalid">
                <mat-icon>{{ isInviting() ? 'hourglass_empty' : 'send' }}</mat-icon>
                <span>{{ isInviting() ? 'Sending...' : 'Send Invite' }}</span>
            </button>
        </div>
    </section>

    <!-- Tabs for Users and Invitees -->
    <mat-tab-group class="admin-tabs" animationDuration="200ms">
        <!-- Current Users Tab -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">group</mat-icon>
                <span>Users ({{ allUsers().length }})</span>
            </ng-template>

            <div class="tab-content">
                <!-- Loading State -->
                @if (isLoadingUsers()) {
                <div class="loading-state">
                    <div class="loading-spinner"></div>
                    <p>Loading users...</p>
                </div>
                } @else {
                <!-- Users Table -->
                @if (allUsers().length > 0) {
                <div class="table-container">
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th class="col-user">User</th>
                                <th class="col-email">Email</th>
                                <th class="col-date">Member Since</th>
                                <th class="col-date">Last Login</th>
                                <th class="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (user of allUsers(); track trackByUser($index, user)) {
                            <tr class="data-row">
                                <td class="col-user">
                                    <div class="user-info">
                                        @if (user.picture) {
                                        <img [src]="user.picture" [alt]="user.name" class="user-avatar" />
                                        } @else {
                                        <div class="user-avatar-fallback">{{ getUserInitials(user.name) }}</div>
                                        }
                                        <span class="user-name">{{ user.name }}</span>
                                    </div>
                                </td>
                                <td class="col-email">
                                    <span class="email-text">{{ user.email }}</span>
                                </td>
                                <td class="col-date">
                                    @if (user.createdAt) {
                                    <span class="date-text">{{ user.createdAt | dfnsFormatDistanceToNowStrict }} ago</span>
                                    } @else {
                                    <span class="date-empty">-</span>
                                    }
                                </td>
                                <td class="col-date">
                                    @if (user.lastLoginAt) {
                                    <span class="date-text">{{ user.lastLoginAt | dfnsFormatDistanceToNowStrict }} ago</span>
                                    } @else {
                                    <span class="date-empty">-</span>
                                    }
                                </td>
                                <td class="col-actions">
                                    <div class="action-buttons">
                                        @if (canDeleteUser(user)) {
                                        <button class="btn-action btn-delete" (click)="deleteUser(user)"
                                            [disabled]="isDeletingUser()"
                                            matTooltip="Remove user">
                                            <mat-icon>person_remove</mat-icon>
                                        </button>
                                        } @else {
                                        <span class="admin-badge" matTooltip="Admin cannot be removed">Admin</span>
                                        }
                                    </div>
                                </td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </div>
                } @else {
                <div class="empty-state">
                    <mat-icon class="empty-icon">group_off</mat-icon>
                    <h3>No users found</h3>
                    <p>No users have signed up yet</p>
                </div>
                }
                }
            </div>
        </mat-tab>

        <!-- Pending Invites Tab -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">schedule_send</mat-icon>
                <span>Pending ({{ pendingInvitees().length }})</span>
            </ng-template>

            <div class="tab-content">
                <!-- Loading State -->
                @if (isLoadingInvitees()) {
                <div class="loading-state">
                    <div class="loading-spinner"></div>
                    <p>Loading invitations...</p>
                </div>
                } @else {
                <!-- Pending Invitees Table -->
                @if (pendingInvitees().length > 0) {
                <div class="table-container">
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th class="col-user">Invitee</th>
                                <th class="col-email">Email</th>
                                <th class="col-date">Invited</th>
                                <th class="col-invitedby">Invited By</th>
                                <th class="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (invitee of pendingInvitees(); track trackByInvitee($index, invitee)) {
                            <tr class="data-row">
                                <td class="col-user">
                                    <div class="user-info">
                                        <div class="user-avatar-fallback pending">
                                            {{ getUserInitials(invitee.name || invitee.email) }}
                                        </div>
                                        <span class="user-name">{{ invitee.name || 'Not provided' }}</span>
                                    </div>
                                </td>
                                <td class="col-email">
                                    <span class="email-text">{{ invitee.email }}</span>
                                </td>
                                <td class="col-date">
                                    <span class="date-text">{{ invitee.invitedAt | dfnsFormatDistanceToNowStrict }} ago</span>
                                </td>
                                <td class="col-invitedby">
                                    <span class="invited-by-text">{{ invitee.invitedByEmail }}</span>
                                </td>
                                <td class="col-actions">
                                    <div class="action-buttons">
                                        <button class="btn-action btn-resend" (click)="resendInvite(invitee)"
                                            matTooltip="Resend invitation">
                                            <mat-icon>send</mat-icon>
                                        </button>
                                        <button class="btn-action btn-delete" (click)="deleteInvite(invitee)"
                                            matTooltip="Revoke invitation">
                                            <mat-icon>delete_outline</mat-icon>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            }
                        </tbody>
                    </table>
                </div>
                } @else {
                <div class="empty-state">
                    <mat-icon class="empty-icon">mark_email_read</mat-icon>
                    <h3>No pending invitations</h3>
                    <p>All invitations have been accepted or no invites have been sent</p>
                </div>
                }
                }
            </div>
        </mat-tab>
    </mat-tab-group>

    <!-- Backfill Section (Admin Only) -->
    <section class="backfill-section">
        <h2 class="section-title">
            <mat-icon>cloud_download</mat-icon>
            Backfill Templates from SES
        </h2>
        <p class="section-description">
            Import existing email templates from AWS SES into Firestore. This will create entries for templates
            with the "me-" prefix that don't already exist in the database.
        </p>
        <div class="backfill-actions">
            <button class="btn-backfill btn-dry-run" (click)="runBackfill(true)" [disabled]="isBackfilling()">
                <mat-icon>{{ isBackfilling() ? 'hourglass_empty' : 'preview' }}</mat-icon>
                <span>{{ isBackfilling() ? 'Running...' : 'Dry Run (Preview)' }}</span>
            </button>
            <button class="btn-backfill btn-execute" (click)="runBackfill(false)" [disabled]="isBackfilling()">
                <mat-icon>{{ isBackfilling() ? 'hourglass_empty' : 'cloud_sync' }}</mat-icon>
                <span>{{ isBackfilling() ? 'Running...' : 'Execute Backfill' }}</span>
            </button>
        </div>

        @if (backfillResult()) {
        <div class="backfill-result" [class.success]="backfillResult().success" [class.error]="!backfillResult().success">
            <h4>{{ backfillResult().success ? 'Result' : 'Error' }}</h4>
            @if (backfillResult().data?.summary) {
            <div class="result-stats">
                <p><strong>Templates found:</strong> {{ backfillResult().data.summary.totalSESTemplates }}</p>
                <p><strong>Template groups:</strong> {{ backfillResult().data.summary.uniqueGroups }}</p>
                <p><strong>Already exist:</strong> {{ backfillResult().data.summary.skipped }}</p>
                <p><strong>{{ backfillResult().data.dryRun ? 'Would create' : 'Created' }}:</strong> {{ backfillResult().data.dryRun ? backfillResult().data.summary.wouldCreate : backfillResult().data.summary.created }}</p>
            </div>
            @if (backfillResult().data.results && backfillResult().data.results.length > 0) {
            <div class="template-list">
                <h5>Templates ({{ backfillResult().data.results.length }}):</h5>
                <ul>
                    @for (result of backfillResult().data.results; track result.baseName) {
                    <li [class.skipped]="result.status === 'skipped'" [class.created]="result.status === 'created'">
                        <strong>{{ result.baseName }}</strong>
                        <span class="status-chip" [class.status-skipped]="result.status === 'skipped'" [class.status-created]="result.status === 'created'">
                            {{ result.status }}
                        </span>
                        @if (result.reason) {
                        <span class="reason">{{ result.reason }}</span>
                        }
                    </li>
                    }
                </ul>
            </div>
            }
            } @else {
            <p>{{ backfillResult().message }}</p>
            }
        </div>
        }
    </section>
</div>
